import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import getUserCourseWithLessons from '@/lib/api/get-user-course-with-lessons';
import { Lesson, UserCourse } from '@/types/user-course-lessons';
import { useAuthUser } from '@/lib/auth/common/useSession';
import { CourseLesson } from '@/types/course';
// import { useCoursesAndLessonsStore } from '@/store/store';

interface UserActivityParams {
  courseEntryUid: string;
  lessons?: CourseLesson[];
  lessonEntryUid?: string;
  /** When true, always fetch on mount (no stale/cache); use with staleTime: 0 for quiz/progress */
  refetchOnMount?: boolean;
  /** Stale time in ms; use 0 to always fetch (no cache) */
  staleTime?: number;
}

const useUserGetCourseWithLessons = ({
  courseEntryUid,
  lessons,
  lessonEntryUid,
  refetchOnMount = false,
  staleTime = 5 * 60 * 1000, // default 5 minutes
}: UserActivityParams) => {
  // Reactive to login/logout (userDataUpdated/userLoggedOut events via SessionHydrator) —
  // a plain isUserLoggedIn() snapshot only re-evaluates when this component happens to
  // re-render for some other reason, so a login/logout that doesn't trigger one (e.g.
  // PartnerSessionSync's in-place sync, or router.refresh() without a remount) left this
  // query's `enabled` stuck at whatever it was on first render — course/lesson data never
  // loaded in, or kept showing the previous user's, until a hard page reload.
  const { isLoggedIn } = useAuthUser();

  const {
    data: apiCourseData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['user-course-lessons', courseEntryUid],
    queryFn: async () => {
      const response = await getUserCourseWithLessons(courseEntryUid);

      if (!response?.data) {
        throw new Error('No data received from API');
      }

      return response.data;
    },
    enabled: !!courseEntryUid && isLoggedIn, // Only fetch if logged in
    staleTime,
    refetchOnMount: refetchOnMount ? 'always' : true, // always fetch on mount when requested (no stale)
    retry: 1,
  });

  const mergedData = useMemo(() => {
    if (!apiCourseData || !isLoggedIn) return { data: null, userLessonData: null };
    const userLessonsArray = apiCourseData.lessons || [];
    // Map through CMS lessons and merge with user data
    const mappedLessons = lessons?.map((lesson: CourseLesson, index: number) => {
      const lessonData = {
        ...lesson,
        index: index + 1,
        nextUrl: `/courses${apiCourseData?.url}${lesson?.url}`,
        next: true,
        title: `${index + 1}. ${lesson?.title}`,
        islast: index === lessons?.length - 1,
      };
      const userLesson = userLessonsArray.find(
        (userLesson: Lesson) => userLesson.entryUid === lesson.uid
      );

      return {
        ...lessonData,
        ...userLesson,
      };
    });

    // Find the current lesson
    const userLessonData = lessonEntryUid
      ? mappedLessons?.find(
          (lesson: CourseLesson & Partial<Lesson>) => lesson.entryUid === lessonEntryUid
        ) || null
      : null;

    // Return merged data
    const mergedCourseData: UserCourse = {
      ...apiCourseData,
      lessons: mappedLessons || [],
    };

    // useCoursesAndLessonsStore.setState((state) => ({
    //   coursesAndLessons: mergedCourseData,
    //   userLessonData: userLessonData,
    // }));

    return { data: mergedCourseData, userLessonData };
  }, [apiCourseData, lessons, lessonEntryUid, isLoggedIn]);

  return {
    userCourseData: mergedData?.data as UserCourse | null,
    userLessonData: mergedData?.userLessonData || null,
    isLoading: isLoading && isLoggedIn,
    error: error as Error | null,
  };
};

export default useUserGetCourseWithLessons;
